/* eslint-disable prefer-const */
import invariant from "tiny-invariant";

import Big from "./Big";
import Decimal from "./Decimal";
import Rounding from "./Rounding";

const toSignificantRounding = {
  [Rounding.ROUND_DOWN]: Decimal.ROUND_DOWN,
  [Rounding.ROUND_HALF_UP]: Decimal.ROUND_HALF_UP,
  [Rounding.ROUND_UP]: Decimal.ROUND_UP,
};

const toFixedRounding = {
  [Rounding.ROUND_DOWN]: Big.roundDown,
  [Rounding.ROUND_HALF_UP]: Big.roundHalfUp,
  [Rounding.ROUND_UP]: Big.roundUp,
};

class Fraction {
  public readonly numerator: bigint;
  public readonly denominator: bigint;

  public constructor(
    numerator: bigint | number,
    denominator: number | bigint = 1n,
  ) {
    this.numerator =
      typeof numerator !== "bigint" ? BigInt(numerator) : numerator;
    this.denominator =
      typeof denominator !== "bigint" ? BigInt(denominator) : denominator;
  }

  private static tryParseFraction(
    fractionish: bigint | number | Fraction,
  ): Fraction {
    if (
      typeof fractionish === "number" ||
      typeof fractionish === "string" ||
      typeof fractionish === "bigint"
    )
      return new Fraction(fractionish);

    if ("numerator" in fractionish && "denominator" in fractionish)
      return fractionish;
    throw new Error("Could not parse fraction");
  }

  // performs floor division
  public get quotient(): bigint {
    return this.numerator / this.denominator;
  }

  // remainder after floor division
  public get remainder(): Fraction {
    return new Fraction(this.numerator % this.denominator, this.denominator);
  }

  public invert(): Fraction {
    return new Fraction(this.denominator, this.numerator);
  }

  public add(other: Fraction | bigint | number): Fraction {
    const otherParsed = Fraction.tryParseFraction(other);
    if (this.denominator === otherParsed.denominator) {
      return new Fraction(
        this.numerator + otherParsed.numerator,
        this.denominator,
      );
    }
    return new Fraction(
      this.numerator * otherParsed.denominator +
        otherParsed.numerator * this.denominator,
      this.denominator * otherParsed.denominator,
    );
  }

  public subtract(other: Fraction | bigint | number): Fraction {
    const otherParsed = Fraction.tryParseFraction(other);
    if (this.denominator === otherParsed.denominator) {
      return new Fraction(
        this.numerator - otherParsed.numerator,
        this.denominator,
      );
    }
    return new Fraction(
      this.numerator * otherParsed.denominator -
        otherParsed.numerator * this.denominator,
      this.denominator * otherParsed.denominator,
    );
  }

  public lessThan(other: Fraction | bigint | number): boolean {
    const otherParsed = Fraction.tryParseFraction(other);
    return (
      BigInt(this.numerator) * BigInt(otherParsed.denominator) <
      BigInt(otherParsed.numerator) * BigInt(this.denominator)
    );
  }

  public equalTo(other: Fraction | bigint | number): boolean {
    const otherParsed = Fraction.tryParseFraction(other);
    return (
      this.numerator * otherParsed.denominator ===
      otherParsed.numerator * this.denominator
    );
  }

  public greaterThan(other: Fraction | bigint | number): boolean {
    const otherParsed = Fraction.tryParseFraction(other);
    return (
      this.numerator * otherParsed.denominator >
      otherParsed.numerator * this.denominator
    );
  }

  public multiply(other: Fraction | bigint | number): Fraction {
    const otherParsed = Fraction.tryParseFraction(other);
    return new Fraction(
      this.numerator * otherParsed.numerator,
      this.denominator * otherParsed.denominator,
    );
  }

  public divide(other: Fraction | bigint | number): Fraction {
    const otherParsed = Fraction.tryParseFraction(other);
    return new Fraction(
      this.numerator * otherParsed.denominator,
      this.denominator * otherParsed.numerator,
    );
  }

  public toSignificant(
    significantDigits: number,
    format: { groupSeparator: "" },
    rounding: Rounding = Rounding.ROUND_HALF_UP,
  ): string {
    invariant(
      Number.isInteger(significantDigits),
      `${significantDigits} is not an integer.`,
    );
    invariant(significantDigits > 0, `${significantDigits} is not positive.`);

    Decimal.set({
      precision: significantDigits + 1,
      rounding: toSignificantRounding[rounding],
    });

    const quotient = new Decimal(this.numerator.toString())
      .div(this.denominator.toString())
      .toSignificantDigits(significantDigits);

    const decimalPlaces = quotient.decimalPlaces();
    let [integerPart, fractionalPart] = quotient
      .toFixed(decimalPlaces)
      .split(".");

    // Apply group separator to integer part
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (format?.groupSeparator) {
      integerPart = integerPart?.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        format.groupSeparator,
      );
    }

    // Combine integer and fractional parts
    return fractionalPart
      ? `${integerPart}.${fractionalPart}`
      : `${integerPart}`;
  }

  public toFixed(
    decimalPlaces: number,
    format: { groupSeparator?: string } = { groupSeparator: "" },
    rounding: Rounding = Rounding.ROUND_HALF_UP,
  ): string {
    invariant(
      Number.isInteger(decimalPlaces),
      `${decimalPlaces} is not an integer.`,
    );
    invariant(decimalPlaces >= 0, `${decimalPlaces} is negative.`);

    Big.DP = decimalPlaces;
    Big.RM = toFixedRounding[rounding];

    const result = new Big(this.numerator.toString())
      .div(this.denominator.toString())
      .toFixed(decimalPlaces);

    // If no group separator, return the result as is
    if (!format.groupSeparator) {
      return result;
    }

    // Split the result into integer and decimal parts
    const [integerPart, decimalPart] = result.split(".");

    // Format the integer part with the group separator
    const formattedIntegerPart = integerPart?.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      format.groupSeparator,
    );

    return decimalPart
      ? `${formattedIntegerPart}.${decimalPart}`
      : `${formattedIntegerPart}`;
  }

  public toJSON() {
    return {
      n: this.numerator.toString(),
      d: this.denominator.toString(),
    };
  }

  /**
   * Helper method for converting any super class back to a fraction
   */
  public get asFraction(): Fraction {
    return new Fraction(this.numerator, this.denominator);
  }
}

export default Fraction;
